import { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Link, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { wordpressApi } from '../../services/integrations';
import type { WordPressConnectionStatus, WordPressTerm } from '@traaker/shared';

// Publish-to-WordPress dialog. Connect-in-context: when the brand has no WP
// connection, the dialog shows the connect form; once connected it shows the
// publish options (draft/publish + category picker).
export function PublishWordPressDialog({
  open, onClose, brandId, title, articleMarkdown,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  title: string;
  articleMarkdown: string;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WordPressConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setStatus(await wordpressApi.status(brandId)); }
    catch (e: any) { setError(e.message || t('newsPublish.error.generic')); }
    finally { setLoading(false); }
  }, [brandId, t]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('newsPublish.title')}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        ) : status?.connected ? (
          <PublishForm brandId={brandId} title={title} articleMarkdown={articleMarkdown}
            siteUrl={status.siteUrl!} onDisconnect={load} onClose={onClose} />
        ) : (
          <ConnectForm brandId={brandId} onConnected={load} />
        )}
      </DialogContent>
      {!status?.connected && !loading && (
        <DialogActions><Button onClick={onClose}>{t('newsPublish.close')}</Button></DialogActions>
      )}
    </Dialog>
  );
}

function ConnectForm({ brandId, onConnected }: { brandId: string; onConnected: () => void }) {
  const { t } = useTranslation();
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [applicationPassword, setApplicationPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const connect = async () => {
    setBusy(true); setErr('');
    try {
      await wordpressApi.connect({ brandId, siteUrl, username, applicationPassword });
      onConnected();
    } catch (e: any) { setErr(e.message || t('newsPublish.error.connect')); }
    finally { setBusy(false); }
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {t('newsPublish.connect.intro')}
      </Typography>
      <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { fontSize: 13 } }}>
        <Trans i18nKey="newsPublish.connect.help" components={{ b: <b /> }} />
      </Alert>
      {err && <Alert severity="error">{err}</Alert>}
      <TextField label={t('newsPublish.connect.siteUrl')} placeholder="https://monsite.com" size="small" fullWidth
        value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
      <TextField label={t('newsPublish.connect.username')} size="small" fullWidth
        value={username} onChange={(e) => setUsername(e.target.value)} />
      <TextField label={t('newsPublish.connect.appPassword')} size="small" fullWidth
        value={applicationPassword} onChange={(e) => setApplicationPassword(e.target.value)}
        helperText={t('newsPublish.connect.appPasswordHelper')} />
      <Button variant="contained" onClick={connect} disabled={busy || !siteUrl || !username || !applicationPassword}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
        {t('newsPublish.connect.submit')}
      </Button>
    </Stack>
  );
}

function PublishForm({
  brandId, title, articleMarkdown, siteUrl, onDisconnect, onClose,
}: {
  brandId: string; title: string; articleMarkdown: string; siteUrl: string; onDisconnect: () => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [wpStatus, setWpStatus] = useState<'draft' | 'publish'>('draft');
  const [categories, setCategories] = useState<WordPressTerm[]>([]);
  const [selectedCats, setSelectedCats] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [published, setPublished] = useState<{ url: string; status: string } | null>(null);

  useEffect(() => {
    wordpressApi.categories(brandId).then(setCategories).catch(() => setCategories([]));
  }, [brandId]);

  const doPublish = async () => {
    setBusy(true); setErr('');
    try {
      const res = await wordpressApi.publish({ brandId, title, articleMarkdown, status: wpStatus, categoryIds: selectedCats });
      setPublished({ url: res.postUrl, status: res.status });
    } catch (e: any) { setErr(e.message || t('newsPublish.error.publish')); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    await wordpressApi.disconnect(brandId).catch(() => {});
    onDisconnect();
  };

  if (published) {
    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Alert severity="success">
          {published.status === 'publish' ? t('newsPublish.published') : t('newsPublish.draftCreated')}
        </Alert>
        <Button variant="outlined" href={published.url} target="_blank" rel="noopener" startIcon={<OpenInNewIcon />}>
          {t('newsPublish.viewOnWordPress')}
        </Button>
        <Button onClick={onClose}>{t('newsPublish.close')}</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        <Trans i18nKey="newsPublish.connectedTo" values={{ siteUrl }} components={{ b: <b />, disconnect: <Link component="button" type="button" onClick={disconnect} /> }} />
      </Typography>
      {err && <Alert severity="error">{err}</Alert>}

      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: .5 }}>{t('newsPublish.statusLabel')}</Typography>
        <RadioGroup row value={wpStatus} onChange={(e) => setWpStatus(e.target.value as 'draft' | 'publish')}>
          <FormControlLabel value="draft" control={<Radio size="small" />} label={t('newsPublish.draft')} />
          <FormControlLabel value="publish" control={<Radio size="small" />} label={t('newsPublish.publishedOption')} />
        </RadioGroup>
      </Box>

      {categories.length > 0 && (
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: .5 }}>{t('newsPublish.categories')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
            {categories.map((c) => {
              const on = selectedCats.includes(c.id);
              return (
                <Chip key={c.id} label={c.name} size="small" onClick={() =>
                  setSelectedCats((p) => on ? p.filter((x) => x !== c.id) : [...p, c.id])}
                  color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} />
              );
            })}
          </Box>
        </Box>
      )}

      <Button variant="contained" onClick={doPublish} disabled={busy}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
        {wpStatus === 'publish' ? t('newsPublish.publishNow') : t('newsPublish.createDraft')}
      </Button>
    </Stack>
  );
}
